import React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/Check';
import { format } from 'date-fns';
import ArrowRightIcon from '@material-ui/icons/ArrowForward';
import { Mastercode, Order } from 'types/schema-generated-types';
import MastercodeCard from '../MastercodeCard';
import getOrderNumber from 'components/Orders/utils/get-order-number';
import { ORDER_DATE_FORMAT } from 'components/Orders/utils/constants';

const useStyles = makeStyles((theme: Theme) => {
  return {
    panelSummaryContent: {
      margin: `${theme.spacing(2.5)}px 0`
    },
    headerButton: {
      marginLeft: theme.spacing(),
      marginRight: theme.spacing()
    },
    detailsContainer: {
      paddingTop: 0
    },
    productCardsContainer: {
      marginTop: theme.spacing(2)
    }
  };
});

interface OrderCardProps {
  order: Order;
  onActivateMastercodeClick: (
    order: Order,
    mastercode: Mastercode | null,
    activateAll: boolean
  ) => () => void;
  onTransferMastercodeClick: (
    order: Order,
    mastercode: Mastercode | null,
    activateAll: boolean
  ) => () => void;
}

const OrderCard = (props: OrderCardProps): React.ReactElement => {
  const { order, onActivateMastercodeClick, onTransferMastercodeClick } = props;
  const classes = useStyles();

  const activeMastercodes = order.mastercodes?.filter(m => !m.dateExpired) ?? [];

  return (
    <ExpansionPanel>
      <ExpansionPanelSummary
        aria-controls="order-content"
        id="order-header"
        classes={{ content: classes.panelSummaryContent }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Grid container spacing={0} alignItems="center" justify="space-between">
          <Grid item>
            <Typography variant="h3">Order No. {getOrderNumber(order)}</Typography>
            <Typography variant="subtitle1">
              Order Date: {format(new Date(order.dateOrdered), ORDER_DATE_FORMAT)}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              startIcon={<ArrowRightIcon />}
              className={classes.headerButton}
              onClick={onTransferMastercodeClick(order, null, true)}
            >
              Transfer All
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<CheckIcon />}
              className={classes.headerButton}
              onClick={onActivateMastercodeClick(order, null, true)}
            >
              Activate All
            </Button>
          </Grid>
        </Grid>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.detailsContainer}>
        <Grid container spacing={0} direction="column">
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Grid container spacing={3} className={classes.productCardsContainer}>
              {activeMastercodes.map(
                (mastercode: Mastercode): React.ReactElement => (
                  <Grid item key={mastercode.code} xs={12}>
                    <MastercodeCard
                      mastercode={mastercode}
                      onActivateMastercodeClick={onActivateMastercodeClick(
                        order,
                        mastercode,
                        false
                      )}
                      onTransferMastercodeClick={onTransferMastercodeClick(
                        order,
                        mastercode,
                        false
                      )}
                    />
                  </Grid>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

export default OrderCard;
